// components/StockForm.tsx
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  ShowChart as StockIcon
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material';
import React from 'react';
import {
  CompactTextFieldProps,
  ExpandedSections,
  SectionHeaderProps,
  SectionKey,
  StockForm
} from '../simulationTypes';

interface StockFormComponentProps {
  form: StockForm;
  handleInputChange: (field: keyof StockForm, value: number | string) => void;
  expandedSections: ExpandedSections;
  toggleSection: (section: SectionKey) => void;
  runStockSimulation: () => void;
  loading: boolean;
  error: string | null;
}

const CompactTextField: React.FC<
  CompactTextFieldProps & { form: StockForm; handleInputChange: any }
> = ({
  label,
  field,
  type = 'number',
  prefix = '',
  suffix = '',
  helperText = '',
  form,
  handleInputChange
}) => (
  <TextField
    label={label}
    type={type}
    size="small"
    fullWidth
    value={type === 'number' && suffix === '%' ? (form[field] as number) * 100 : form[field]}
    onChange={(e) => {
      const value =
        type === 'number'
          ? suffix === '%'
            ? parseFloat(e.target.value) / 100
            : parseFloat(e.target.value)
          : e.target.value;
      handleInputChange(field, value);
    }}
    InputProps={{
      startAdornment: prefix ? (
        <InputAdornment position="start">{prefix}</InputAdornment>
      ) : undefined,
      endAdornment: suffix ? <InputAdornment position="end">{suffix}</InputAdornment> : undefined
    }}
    helperText={helperText}
    sx={{ mb: 1 }}
  />
);

const SectionHeader: React.FC<
  SectionHeaderProps & { expandedSections: ExpandedSections; toggleSection: any }
> = ({ title, section, icon, expandedSections, toggleSection }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      p: 1,
      borderRadius: 1,
      bgcolor: expandedSections[section] ? '#e3f2fd' : 'transparent',
      '&:hover': { bgcolor: '#f5f5f5' }
    }}
    onClick={() => toggleSection(section)}
  >
    {icon}
    <Typography variant="subtitle2" fontWeight="600" sx={{ ml: 1, flex: 1 }}>
      {title}
    </Typography>
    <IconButton size="small">
      {expandedSections[section] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
    </IconButton>
  </Box>
);

const StockFormComponent: React.FC<StockFormComponentProps> = ({
  form,
  handleInputChange,
  expandedSections,
  toggleSection,
  runStockSimulation,
  loading,
  error
}) => {
  return (
    <Card elevation={2} sx={{ height: 'fit-content', position: 'sticky', top: 16 }}>
              <CardContent sx={{ p: { xs: 0.5, sm: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <StockIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6" fontWeight="bold" color="primary">
            Stock Setup
          </Typography>
        </Box>

        {/* Basic Config */}
        <SectionHeader
          title="Basic Config"
          section="basic"
          icon={<StockIcon fontSize="small" />}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
        />
        <Collapse in={expandedSections.basic}>
          <Box sx={{ p: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <CompactTextField
                  label="Iterations"
                  field="iterations"
                  helperText="Monte Carlo runs"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Years"
                  field="years"
                  helperText="Investment period"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12}>
                <CompactTextField
                  label="Stock/Fund Name"
                  field="assetName"
                  type="text"
                  helperText="Asset name"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Purchase Price"
                  field="purchasePrice"
                  prefix="$"
                  helperText="Investment amount"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Brokerage"
                  field="brokerage"
                  prefix="$"
                  helperText="Trading fees"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Box>
        </Collapse>

        {/* Trading Costs */}
        <SectionHeader
          title="Trading Costs"
          section="trading"
          icon={<StockIcon fontSize="small" />}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
        />
        <Collapse in={expandedSections.trading}>
          <Box sx={{ p: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <CompactTextField
                  label="Platform Fees"
                  field="platformFees"
                  prefix="$"
                  helperText="Annual fees"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Setup Costs"
                  field="setupCosts"
                  prefix="$"
                  helperText="Initial costs"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Management Fee"
                  field="managementFeePercent"
                  suffix="%"
                  helperText="Annual fee"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Transaction Cost"
                  field="transactionCostPercent"
                  suffix="%"
                  helperText="Per trade"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Box>
        </Collapse>

        {/* Dividends */}
        <SectionHeader
          title="Dividend Income"
          section="dividends"
          icon={<StockIcon fontSize="small" />}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
        />
        <Collapse in={expandedSections.dividends}>
          <Box sx={{ p: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <CompactTextField
                  label="Annual Dividend"
                  field="annualIncomeAmount"
                  prefix="$"
                  helperText="Expected dividends"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Dividend Growth Min"
                  field="incomeGrowthMin"
                  suffix="%"
                  helperText="Min growth"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Dividend Growth Max"
                  field="incomeGrowthMax"
                  suffix="%"
                  helperText="Max growth"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Box>
        </Collapse>

        {/* Market Growth */}
        <SectionHeader
          title="Market Growth"
          section="market"
          icon={<StockIcon fontSize="small" />}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
        />
        <Collapse in={expandedSections.market}>
          <Box sx={{ p: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <CompactTextField
                  label="Capital Min"
                  field="capitalGrowthMin"
                  suffix="%"
                  helperText="Conservative"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Capital Max"
                  field="capitalGrowthMax"
                  suffix="%"
                  helperText="Optimistic"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Volatility"
                  field="volatilityStdDev"
                  suffix="%"
                  helperText="Market volatility"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Recession %"
                  field="recessionProb"
                  suffix="%"
                  helperText="Probability"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Box>
        </Collapse>

        {/* Risk Factors */}
        <SectionHeader
          title="Risk Factors"
          section="risk"
          icon={<StockIcon fontSize="small" />}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
        />
        <Collapse in={expandedSections.risk}>
          <Box sx={{ p: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <CompactTextField
                  label="Default Risk"
                  field="defaultProbability"
                  suffix="%"
                  helperText="Company default"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Liquidity Discount"
                  field="liquidityDiscount"
                  suffix="%"
                  helperText="Sale discount"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={6}>
                <CompactTextField
                  label="Recession Impact"
                  field="recessionImpact"
                  suffix="%"
                  helperText="Market crash impact"
                  form={form}
                  handleInputChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Box>
        </Collapse>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button
            variant="contained"
            onClick={runStockSimulation}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <StockIcon />}
            fullWidth
            sx={{ fontWeight: 'bold' }}
          >
            {loading ? 'Running Simulation...' : 'Run Stock Simulation'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default StockFormComponent;
